import React, { useState, useEffect } from 'react';
import './App.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { getTrackingInfo } from './firebaseConfig';

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingError, setTrackingError] = useState('');
  const [quoteData, setQuoteData] = useState({
    weight: '',
    distance: '',
    serviceType: 'standard',
    insurance: true
  });
  const [calculatedQuote, setCalculatedQuote] = useState(null);

  // App initialization and scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'services', 'quote', 'tracking', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;
          
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount to set initial section
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    setTrackingLoading(true);
    setTrackingError('');
    setTrackingResult(null);
    
    try {
      // Validate input
      if (!trackingNumber.trim()) {
        setTrackingError('empty');
        setTrackingLoading(false);
        return;
      }
      
      // Trim whitespace and convert to uppercase
      const cleanedNumber = trackingNumber.trim().toUpperCase();
      const result = await getTrackingInfo(cleanedNumber);
      
      if (result) {
        setTrackingResult(result);
        setTrackingError('');
        // Scroll to tracking section
        setTimeout(() => scrollToSection('tracking'), 100);
      } else {
        setTrackingError('not-found');
        setTrackingResult(null);
        // Scroll to tracking section
        setTimeout(() => scrollToSection('tracking'), 100);
      }
    } catch (error) {
      // Validation errors from firebaseConfig
      if (error.message.includes('Invalid shipment')) {
        setTrackingError('invalid-format');
      } else if (error.message.includes('Too many requests')) {
        setTrackingError('rate-limit');
      } else if (error.message.includes('Missing fields') || error.message.includes('Incomplete tracking') || error.message.includes('Timeline') || error.message.includes('Invalid tracking data')) {
        // Data consistency errors - show friendly server error
        console.error('Data validation error:', error.message);
        setTrackingError('server-error');
      } else {
        // Any other unexpected errors
        console.error('Tracking error:', error);
        setTrackingError('server-error');
      }
      console.error('Tracking error:', error);
    } finally {
      setTrackingLoading(false);
    }
  };

  const calculateQuote = (e) => {
    e.preventDefault();
    const weight = parseFloat(quoteData.weight);
    const distance = parseFloat(quoteData.distance);
    
    if (!weight || !distance) {
      alert('Please enter valid weight and distance');
      return;
    }

    // Base rate calculation
    let baseRate = weight * 50; // $50 per kg
    let distanceRate = distance * 2; // $2 per km
    
    // Service type multiplier
    const serviceMultiplier = {
      standard: 1,
      express: 1.5,
      premium: 2
    };
    
    let total = (baseRate + distanceRate) * serviceMultiplier[quoteData.serviceType];
    
    // Insurance (5% of shipment value, estimated at $60,000 per kg)
    const insuranceCost = quoteData.insurance ? weight * 60000 * 0.05 : 0;
    
    total += insuranceCost;
    
    setCalculatedQuote({
      baseRate: baseRate.toFixed(2),
      distanceRate: distanceRate.toFixed(2),
      serviceType: quoteData.serviceType,
      insuranceCost: insuranceCost.toFixed(2),
      total: total.toFixed(2),
      weight: weight,
      distance: distance
    });
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">AnchorLine Global</div>
          <ul className="nav-menu">
            <li>
              <a 
                href="#home" 
                className={activeSection === 'home' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}
              >
                Home
              </a>
            </li>
            <li>
              <a 
                href="#about" 
                className={activeSection === 'about' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}
              >
                About Us
              </a>
            </li>
            <li>
              <a 
                href="#services" 
                className={activeSection === 'services' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}
              >
                Services
              </a>
            </li>
            <li>
              <a 
                href="#quote" 
                className={activeSection === 'quote' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('quote'); }}
              >
                Get Quote
              </a>
            </li>
            <li>
              <a 
                href="#tracking" 
                className={activeSection === 'tracking' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('tracking'); }}
              >
                Tracking
              </a>
            </li>
            <li>
              <a 
                href="#contact" 
                className={activeSection === 'contact' ? 'active' : ''}
                onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
              >
                Contact Us
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Home Section */}
      <section id="home" className="hero-section">
        <div className="hero-overlay">
          <div className="hero-content">
            <h1 className="hero-title">Premium Gold Consignment Shipping</h1>
            <p className="hero-subtitle">Secure, Fast, and Reliable Transportation of Precious Metals</p>
            
            <form onSubmit={handleTrackingSubmit} className="tracking-search">
              <input 
                type="text" 
                placeholder="Enter your tracking number"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                className="tracking-input"
              />
              <button type="submit" className="tracking-button">Track Shipment</button>
            </form>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="about-section">
        <div className="container">
          <h2 className="section-title">About Us</h2>
          <div className="about-content">
            <div className="about-text">
              <h3>Your Trusted Partner in Precious Metal Transportation</h3>
              <p>
                AnchorLine Global Logistics has been the industry leader in secure gold consignment shipping for over 20 years. 
                We specialize in the safe and reliable transportation of precious metals, providing peace of mind to 
                our clients worldwide.
              </p>
              <p>
                Our state-of-the-art security systems, combined with our experienced team and comprehensive insurance 
                coverage, ensure that your valuable consignments are protected every step of the way.
              </p>
              <div className="about-stats">
                <div className="stat-item">
                  <h4>9+</h4>
                  <p>Years Experience</p>
                </div>
                <div className="stat-item">
                  <h4>11K+</h4>
                  <p>Safe Deliveries</p>
                </div>
                <div className="stat-item">
                  <h4>100%</h4>
                  <p>Insured Shipments</p>
                </div>
              </div>
            </div>
            <div className="about-image">
              <img 
                src="/images/gold-storage.jpg" 
                alt="Gold bars secure storage" 
                className="about-img"
              />
            </div>
          </div>
          
          {/* Mission & Vision */}
          <div className="mission-vision">
            <div className="mission-card">
              <div className="mv-icon"><i className="bi bi-bullseye"></i></div>
              <h3>Our Mission</h3>
              <p>
                To provide unparalleled security and reliability in the transportation of precious metals worldwide, 
                ensuring every client's valuable assets reach their destination safely and on time. We are committed 
                to maintaining the highest standards of service excellence and customer satisfaction.
              </p>
            </div>
            <div className="vision-card">
              <div className="mv-icon"><i className="bi bi-compass"></i></div>
              <h3>Our Vision</h3>
              <p>
                To be the world's most trusted and technologically advanced gold consignment shipping company, 
                setting industry standards for security, innovation, and customer service. We envision a future 
                where precious metal transportation is seamless, transparent, and completely worry-free.
              </p>
            </div>
          </div>

          {/* Additional images */}
          <div className="about-gallery">
            <div className="gallery-item">
              <img src="/images/security-truck.jpg" alt="Security truck" />
              <p>Armored Transport Fleet</p>
            </div>
            <div className="gallery-item">
              <img src="/images/team-working.jpg" alt="Team working" />
              <p>Expert Team</p>
            </div>
            <div className="gallery-item">
              <img src="/images/global-network.jpg" alt="Global network" />
              <p>Global Network</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <h2 className="section-title">Our Services</h2>
          <p className="section-subtitle">Comprehensive solutions for all your precious metal shipping needs</p>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-image">
                <img src="/images/security.jpg" alt="Security" />
              </div>
              <div className="service-icon"><i className="bi bi-lock-fill"></i></div>
              <h3>Secure Transportation</h3>
              <p>Military-grade security systems and armed escorts for maximum protection of your gold consignments.</p>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/global.jpg" alt="Global" />
              </div>
              <div className="service-icon"><i className="bi bi-globe"></i></div>
              <h3>Global Shipping</h3>
              <p>Worldwide delivery network with customs clearance expertise for seamless international shipments.</p>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/packaging1.png" alt="Packaging" />
              </div>
              <div className="service-icon"><i className="bi bi-box-seam"></i></div>
              <h3>Custom Packaging</h3>
              <p>Specialized packaging solutions designed specifically for precious metal transportation and storage.</p>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/insurance.jpg" alt="Insurance" />
              </div>
              <div className="service-icon"><i className="bi bi-shield-check"></i></div>
              <h3>Full Insurance</h3>
              <p>Comprehensive insurance coverage for all shipments, protecting your investment at all times.</p>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/tracking.jpg" alt="Tracking" />
              </div>
              <div className="service-icon"><i className="bi bi-telephone-fill"></i></div>
              <h3>Real-Time Tracking</h3>
              <p>Advanced GPS tracking system allowing you to monitor your consignment 24/7 from anywhere.</p>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/express.jpg" alt="Express" />
              </div>
              <div className="service-icon"><i className="bi bi-lightning-charge-fill"></i></div>
              <h3>Express Delivery</h3>
              <p>Priority shipping options for time-sensitive deliveries with guaranteed delivery windows.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Get Quote Section */}
      <section id="quote" className="quote-section">
        <div className="container">
          <h2 className="section-title">Get Instant Quote</h2>
          <p className="section-subtitle-light">Calculate your shipping cost in real-time</p>
          
          <div className="quote-container">
            <div className="quote-form-wrapper">
              <form onSubmit={calculateQuote} className="quote-form">
                <div className="form-group">
                  <label htmlFor="weight">Gold Weight (kg)</label>
                  <input 
                    id="weight"
                    type="number" 
                    step="0.1"
                    placeholder="e.g., 2.5"
                    value={quoteData.weight}
                    onChange={(e) => setQuoteData({...quoteData, weight: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="distance">Distance (km)</label>
                  <input 
                    id="distance"
                    type="number"
                    placeholder="e.g., 500"
                    value={quoteData.distance}
                    onChange={(e) => setQuoteData({...quoteData, distance: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="serviceType">Service Type</label>
                  <select 
                    id="serviceType"
                    value={quoteData.serviceType}
                    onChange={(e) => setQuoteData({...quoteData, serviceType: e.target.value})}
                    className="form-input"
                  >
                    <option value="standard">Standard (3-5 days)</option>
                    <option value="express">Express (1-2 days) +50%</option>
                    <option value="premium">Premium (Same day) +100%</option>
                  </select>
                </div>
                
                <div className="form-group checkbox-group">
                  <label>
                    <input 
                      type="checkbox"
                      checked={quoteData.insurance}
                      onChange={(e) => setQuoteData({...quoteData, insurance: e.target.checked})}
                    />
                    <span>Include Full Insurance (Recommended)</span>
                  </label>
                </div>
                
                <button type="submit" className="form-button">Calculate Quote</button>
              </form>
              
              {calculatedQuote && (
                <div className="quote-result">
                  <h3>Your Estimated Quote</h3>
                  <div className="quote-breakdown">
                    <div className="quote-line">
                      <span>Base Rate ({calculatedQuote.weight} kg):</span>
                      <span>${calculatedQuote.baseRate}</span>
                    </div>
                    <div className="quote-line">
                      <span>Distance ({calculatedQuote.distance} km):</span>
                      <span>${calculatedQuote.distanceRate}</span>
                    </div>
                    <div className="quote-line">
                      <span>Service Type:</span>
                      <span className="capitalize">{calculatedQuote.serviceType}</span>
                    </div>
                    {quoteData.insurance && (
                      <div className="quote-line">
                        <span>Insurance Coverage:</span>
                        <span>${calculatedQuote.insuranceCost}</span>
                      </div>
                    )}
                    <div className="quote-line total">
                      <span>Total Estimate:</span>
                      <span>${calculatedQuote.total}</span>
                    </div>
                  </div>
                  <p className="quote-note">* Final price may vary based on specific requirements</p>
                </div>
              )}
            </div>
            
            <div className="quote-info">
              <h3>Why Choose AnchorLine Global Logistics?</h3>
              <div className="quote-benefits">
                <div className="benefit-item">
                  <span className="benefit-icon"><i className="bi bi-cash-coin"></i></span>
                  <div>
                    <h4>Competitive Pricing</h4>
                    <p>Best rates in the industry with no hidden fees</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon"><i className="bi bi-shield-lock"></i></span>
                  <div>
                    <h4>Maximum Security</h4>
                    <p>Military-grade protection for your assets</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon"><i className="bi bi-lightning-charge-fill"></i></span>
                  <div>
                    <h4>Fast Delivery</h4>
                    <p>Express options available for urgent shipments</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon"><i className="bi bi-graph-up"></i></span>
                  <div>
                    <h4>Real-Time Updates</h4>
                    <p>Track your shipment every step of the way</p>
                  </div>
                </div>
              </div>
              
              <div className="pricing-info">
                <h4>Pricing Structure</h4>
                <ul>
                  <li>Base rate: $50 per kg</li>
                  <li>Distance: $2 per km</li>
                  <li>Insurance: 5% of shipment value</li>
                  <li>Estimated value: $60,000 per kg</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracking Section */}
      <section id="tracking" className="tracking-section">
        <div className="container">
          <h2 className="section-title">Track Your Shipment</h2>
          <div className="tracking-content">
            <div className="tracking-info">
              <h3>Real-Time Shipment Monitoring</h3>
              <p>
                Enter your unique tracking number to get real-time updates on your gold consignment. 
                Our advanced tracking system provides detailed information including current location, 
                estimated delivery time, and shipment status.
              </p>
              <ul className="tracking-features">
                <li>✓ Live GPS tracking</li>
                <li>✓ Delivery notifications</li>
                <li>✓ Historical route data</li>
                <li>✓ Proof of delivery</li>
              </ul>
            </div>
            <div className="tracking-form-container">
              <form onSubmit={handleTrackingSubmit} className="tracking-form">
                <label htmlFor="tracking-input">Tracking Number</label>
                <input 
                  id="tracking-input"
                  type="text" 
                  placeholder="e.g., GS123456789"
                  value={trackingNumber}
                  onChange={(e) => {
                    setTrackingNumber(e.target.value);
                    // Clear previous results when user starts typing a new number
                    if (trackingResult) {
                      setTrackingResult(null);
                    }
                  }}
                  className="form-input"
                  required
                />
                <button type="submit" className="form-button">Track Now</button>
              </form>
              {trackingLoading && (
                <div className="tracking-loading">
                  <div className="loading-spinner"></div>
                  <p>Searching for your shipment...</p>
                </div>
              )}
              
              {trackingResult && !trackingResult.error && !trackingLoading && (
                <div className="tracking-results">
                  <div className="tracking-header">
                    <h3>Shipment Details</h3>
                    <span className={`status-badge ${trackingResult.status.toLowerCase().replace(' ', '-')}`}>
                      {trackingResult.status}
                    </span>
                  </div>
                  
                  <div className="tracking-details">
                    <div className="detail-row">
                      <span className="detail-label">Tracking Number:</span>
                      <span className="detail-value">{trackingNumber.toUpperCase()}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Current Location:</span>
                      <span className="detail-value">{trackingResult.currentLocation}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Origin:</span>
                      <span className="detail-value">{trackingResult.origin}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Destination:</span>
                      <span className="detail-value">{trackingResult.destination}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Weight:</span>
                      <span className="detail-value">{trackingResult.weight}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Estimated Delivery:</span>
                      <span className="detail-value">{trackingResult.estimatedDelivery}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Last Update:</span>
                      <span className="detail-value">{trackingResult.lastUpdate}</span>
                    </div>
                  </div>
                  
                  <div className="tracking-timeline">
                    <h4>Shipment Timeline</h4>
                    {trackingResult.timeline.map((event, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <div className="timeline-date">{event.date} - {event.time}</div>
                          <div className="timeline-status">{event.status}</div>
                          <div className="timeline-location">{event.location}</div>
                          {event.note && (
                            <div className="timeline-note">
                              <i className="bi bi-chat-left-text"></i> {event.note}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {trackingError && !trackingLoading && (
                <div className="tracking-error">
                  {trackingError === 'not-found' && (
                    <>
                      <div className="error-icon">❌</div>
                      <h4>No Shipment Found</h4>
                      <p>We couldn't find a package with tracking number <strong>{trackingNumber.toUpperCase()}</strong></p>
                      <p className="error-help">Please double-check your tracking number and try again. Tracking numbers start with "GS" followed by 9 digits (example: GS123456789)</p>
                      <div className="contact-support">
                        <p><strong>Need Help?</strong></p>
                        <p>Contact our support team at:</p>
                        <a href="tel:+14633084731" className="contact-link">📞 +1 (463) 308-4731</a>
                        <p>Our team is available 24/7 to assist you.</p>
                      </div>
                    </>
                  )}
                  
                  {trackingError === 'invalid-format' && (
                    <>
                      <div className="error-icon">⚠️</div>
                      <h4>Invalid Tracking Number Format</h4>
                      <p>Tracking numbers must be in the format: <strong>GS + 9 digits</strong></p>
                      <p className="error-help">Examples: GS123456789, GS000000001, GS999999999</p>
                      <p>You entered: <strong>{trackingNumber}</strong></p>
                    </>
                  )}
                  
                  {trackingError === 'rate-limit' && (
                    <>
                      <div className="error-icon">⏱️</div>
                      <h4>Too Many Requests</h4>
                      <p>You've made too many tracking requests too quickly.</p>
                      <p className="error-help">Please wait a moment and try again.</p>
                    </>
                  )}
                  
                  {trackingError === 'empty' && (
                    <>
                      <div className="error-icon">📝</div>
                      <h4>Enter a Tracking Number</h4>
                      <p>Please enter your tracking number in the form above.</p>
                    </>
                  )}
                  
                  {trackingError === 'server-error' && (
                    <>
                      <div className="error-icon">🔧</div>
                      <h4>Server Error</h4>
                      <p>Something went wrong while retrieving your shipment information.</p>
                      <p className="error-help">Please try again in a moment. If the problem persists, contact our support team:</p>
                      <a href="tel:+14633084731" className="contact-link">📞 +1 (463) 308-4731</a>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <h2 className="section-title">Contact Us</h2>
          <div className="contact-content">
            <div className="contact-info">
              <h3>Get in Touch</h3>
              <p>
                Have questions about our services? Need a quote for your gold consignment? 
                Our team is here to help you 24/7.
              </p>
              <div className="contact-details">
                <div className="contact-item">
                  <span className="contact-icon"><i className="bi bi-telephone-fill"></i></span>
                  <div>
                    <h4>Phone</h4>
                    <p>+14633084731</p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon"><i className="bi bi-envelope-fill"></i></span>
                  <div>
                    <h4>Email</h4>
                    <p>info@anchorlineglobal.com</p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon"><i className="bi bi-geo-alt-fill"></i></span>
                  <div>
                    <h4>Main Office</h4>
                    <p>8506 Friendship Ln, Indianapolis, IN 46217<br/>United States</p>
                  </div>
                </div>
                <div className="contact-item">
                  <span className="contact-icon"><i className="bi bi-clock-fill"></i></span>
                  <div>
                    <h4>Business Hours</h4>
                    <p>24/7 Customer Support</p>
                  </div>
                </div>
              </div>
              
              <div className="warehouses-section">
                <h4 style={{marginTop: '30px', marginBottom: '15px'}}>Global Warehouse Locations</h4>
                <div className="warehouse-list">
                  <div className="warehouse-item">
                    <span className="warehouse-icon"><i className="bi bi-building"></i></span>
                    <div>
                      <h5>United States</h5>
                      <p>8506 Friendship Ln, Indianapolis, IN 46217</p>
                    </div>
                  </div>
                  <div className="warehouse-item">
                    <span className="warehouse-icon"><i className="bi bi-building"></i></span>
                    <div>
                      <h5>Germany</h5>
                      <p>1119 Grandview Dr, Berlin, PA 15530</p>
                    </div>
                  </div>
                  <div className="warehouse-item">
                    <span className="warehouse-icon"><i className="bi bi-building"></i></span>
                    <div>
                      <h5>United Kingdom</h5>
                      <p>1521 Spaulding Ishkooda Rd, Birmingham, AL 35211</p>
                    </div>
                  </div>
                  <div className="warehouse-item">
                    <span className="warehouse-icon"><i className="bi bi-building"></i></span>
                    <div>
                      <h5>Canada</h5>
                      <p>6179 State Route 213, Toronto, OH 43964</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="contact-form-container">
              <form className="contact-form">
                <input type="text" placeholder="Your Name" className="form-input" />
                <input type="email" placeholder="Your Email" className="form-input" />
                <input type="tel" placeholder="Phone Number" className="form-input" />
                <textarea placeholder="Your Message" rows="5" className="form-input"></textarea>
                <button type="submit" className="form-button">Send Message</button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            {/* Company Info */}
            <div className="footer-section">
              <h3 className="footer-logo">AnchorLine Global Logistics</h3>
              <p className="footer-description">
                Your trusted partner in secure precious metal transportation worldwide.
              </p>
            </div>

            {/* Quick Links */}
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul className="footer-links">
                <li><a href="#home">Home</a></li>
                <li><a href="#about">About Us</a></li>
                <li><a href="#services">Services</a></li>
                <li><a href="#quote">Get Quote</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div className="footer-section">
              <h4>Contact</h4>
              <div className="footer-contact">
                <div className="footer-contact-item">
                  <i className="bi bi-telephone"></i>
                  <a href="tel:+14633084731">+1 (463) 308-4731</a>
                </div>
                <div className="footer-contact-item">
                  <i className="bi bi-envelope"></i>
                  <a href="mailto:info@anchorlineglobal.com">info@anchorlineglobal.com</a>
                </div>
                <div className="footer-contact-item">
                  <i className="bi bi-geo-alt"></i>
                  <span>Indianapolis, IN 46217</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <p>&copy; 2026 AnchorLine Global Logistics. All rights reserved.</p>
            <div className="footer-legal">
              <a href="#privacy">Privacy</a>
              <span>|</span>
              <a href="#terms">Terms</a>
              <span>|</span>
              <a href="#contact">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
